// @ts-check
// Editing a cart line whose product has no overlay: the product page IS the editor. The line's own
// choices come back into the buy form, and the add that follows REPLACES the line it came from.

import { propertyName } from '@theme/custom.customizer-handle';
import { ThemeEvents } from '@theme/events';

/**
 * The cart machinery the overlay's own edit path already owns, loaded only once a visit turns out
 * to BE an edit: this module ships on every product page, and nothing here runs without the param.
 * @returns {Promise<typeof import('@theme/custom.customizer-cart-edit')>}
 */
const cartEdit = () => import('@theme/custom.customizer-cart-edit');

/**
 * The query param snippets/custom.cart-item-links.liquid puts on Edit for a product with no
 * overlay. Deliberately not the overlay's `customizer_line`: that one boots the customizer, and a
 * product that has none would swallow the edit silently.
 */
export const CART_LINE_PARAM = 'cart_line';

/** The page's own buy form - the same lookup assets/custom.customizer.js submits through. */
const FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';

/** @typedef {import('@theme/custom.customizer-cart-edit').Cart} Cart */
/** @typedef {import('@theme/custom.customizer-cart-edit').CartLine} CartLine */

/**
 * What the buy form submitted, in the shape a cart line can be compared against.
 * @typedef {object} Submission
 * @property {string} variantId
 * @property {number} quantity
 * @property {Record<string, string>} properties - Shopper-facing only.
 */

/** The line key this page visit is editing, or null on an ordinary product-page visit. */
let editing = /** @type {string | null} */ (null);

/** The last submission through the buy form, read before the add so the new line can be found. */
let submitted = /** @type {Submission | null} */ (null);

/**
 * A line's shopper-facing properties. The private ones are the theme's own record and Shopify drops
 * an empty one from the line, so neither can take part in matching a line to a submission.
 * @param {Record<string, string> | undefined} properties
 * @returns {Record<string, string>}
 */
export function publicProperties(properties) {
  /** @type {Record<string, string>} */
  const shown = {};
  for (const [key, value] of Object.entries(properties ?? {})) {
    if (key.startsWith('_') || typeof value !== 'string' || value === '') continue;
    shown[propertyName(key)] = value;
  }
  return shown;
}

/**
 * Whether this cart line is the one the form just submitted: same variant, same shopper-facing
 * properties. `_config` is deliberately not compared - the inline block rebuilds it on every
 * keystroke (assets/custom.name-jewelry.js), so the same choices can carry a different record.
 * @param {CartLine | undefined} line
 * @param {Submission} submission
 * @returns {boolean}
 */
export function matchesSubmission(line, submission) {
  if (String(line?.variant_id ?? '') !== submission.variantId) return false;
  const shown = publicProperties(line?.properties);
  const keys = Object.keys(submission.properties);
  if (Object.keys(shown).length !== keys.length) return false;
  return keys.every((key) => shown[key] === submission.properties[key]);
}

/**
 * What the add left the cart in, and so what has to happen to the line being edited.
 *
 * `merged` is the case a plain add-then-remove would get wrong: Shopify folds an add that matches
 * an existing line into it, so a shopper who opened Edit and changed nothing lands back on the
 * SAME line, now carrying both quantities. Removing it there would throw away the line they were
 * editing, and leaving it would double their order.
 * @param {Cart | null | undefined} cart
 * @param {string} key - The line being edited.
 * @param {Submission} submission
 * @returns {{ action: 'replace' | 'merged', line: CartLine } | { action: 'none' }}
 */
export function planReplacement(cart, key, submission) {
  const items = cart?.items ?? [];
  const replacement = items.find((item) => item.key !== key && matchesSubmission(item, submission));
  if (replacement) return { action: 'replace', line: replacement };
  const edited = items.find((item) => item.key === key);
  if (edited && matchesSubmission(edited, submission)) return { action: 'merged', line: edited };
  return { action: 'none' };
}

/**
 * The line key the current URL asks to edit, or null.
 * @param {string} [search] - Defaults to the live query string.
 * @returns {string | null}
 */
export function cartLineKey(search = window.location.search) {
  const key = new URLSearchParams(search).get(CART_LINE_PARAM);
  return key && key.trim() !== '' ? key : null;
}

/**
 * Read the buy form the way Shopify's add endpoint will.
 * @param {HTMLFormElement} form
 * @returns {Submission}
 */
function readSubmission(form) {
  const data = new FormData(form);
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [name, value] of data.entries()) {
    const key = /^properties\[(.+)\]$/.exec(name)?.[1];
    if (!key || typeof value !== 'string' || key.startsWith('_') || value === '') continue;
    properties[propertyName(key)] = value;
  }
  const id = data.get('id');
  return {
    variantId: typeof id === 'string' ? id : '',
    quantity: Number(data.get('quantity')) || 1,
    properties,
  };
}

/**
 * Make the buy button read and behave as Update: its own copy, and none of the add-to-cart theatre.
 *
 * The copy rides on Theme.translations rather than a data attribute because the button is stock
 * markup that a variant change re-renders from the server. `data-puppet` is the flag
 * assets/product-form.js already reads to skip the fly-to-cart image and the "Added" flourish
 * (assets/sticky-add-to-cart.js sets the same one): an update is not an add, and both animations
 * would play over a page that is about to hand the shopper back to the cart.
 * @param {HTMLFormElement} form
 */
function dressAsUpdate(form) {
  const label = Theme.translations.cart_item_update;
  for (const button of form.querySelectorAll('[ref="addToCartButton"]')) {
    if (button instanceof HTMLElement) button.dataset.puppet = 'true';
  }
  if (!label) return;
  for (const text of form.querySelectorAll('.add-to-cart-text__content')) {
    // The copy sits in the innermost span; the wrappers carry the button's animation.
    const target = text.querySelector('span span') ?? text;
    if (target.textContent?.trim() !== label) target.textContent = label;
  }
  // The sticky bar puppets this same button, so its pill has to say the same thing. It sits
  // outside the form, and its label is the pill's own span rather than the stock add-to-cart one.
  for (const pill of document.querySelectorAll('sticky-add-to-cart .custom-cta-pill__label')) {
    if (pill.textContent?.trim() !== label) pill.textContent = label;
  }
}

/**
 * Keep the cart drawer shut for this visit. Its auto-open fires on the add, and settling the
 * replacement then navigates to the cart page underneath it - a drawer that slides in only to be
 * thrown away. The attribute is dropped from this rendering only; the next page load brings the
 * merchant's setting back.
 */
function quietTheDrawer() {
  document.querySelector('cart-drawer-component[auto-open]')?.removeAttribute('auto-open');
}

/**
 * Hold the buy buttons while the replacement settles, so the press has a visible answer: the
 * update's own round trips take about a second, and with the add animation gone the button would
 * otherwise sit there looking untouched.
 * @param {boolean} busy
 */
function markBusy(busy) {
  for (const button of document.querySelectorAll('[ref="addToCartButton"]')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    // Only ours to re-enable: a button the page disabled itself (sold out, no variant chosen)
    // must stay that way when the hold is released.
    if (busy && !button.disabled) {
      button.disabled = true;
      button.dataset.cartLineBusy = 'true';
    } else if (!busy && button.dataset.cartLineBusy === 'true') {
      delete button.dataset.cartLineBusy;
      button.disabled = false;
    }
  }
}

/**
 * Put the line's own answers back into the form controls that collect them.
 * @param {HTMLFormElement} form
 * @param {CartLine} line
 */
function restore(form, line) {
  const values = publicProperties(line.properties);
  for (const element of form.elements) {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) continue;
    if (element.type === 'checkbox' || element.type === 'radio' || element.type === 'hidden') {
      continue;
    }
    const property = /^properties\[(.+)\]$/.exec(element.name)?.[1];
    if (property) setValue(element, values[propertyName(property)]);
    else if (element.name === 'quantity' && typeof line.quantity === 'number') {
      setValue(element, String(line.quantity));
    }
  }
}

/**
 * The line's answers that no control on this page collects. An Order Comments note is the everyday
 * one: the customizer's Review step collects it, so the product page has nowhere to put it.
 * @param {CartLine} line
 * @param {string[]} claimed - Property names the buy form already submits.
 * @returns {Record<string, string>}
 */
export function unclaimedProperties(line, claimed) {
  /** @type {Record<string, string>} */
  const carried = {};
  for (const [property, value] of Object.entries(publicProperties(line.properties))) {
    if (!claimed.includes(property)) carried[property] = value;
  }
  return carried;
}

/**
 * Send those answers along with the add. Without this the replacement line loses them and the
 * removal that follows takes the only copy with it, so a shopper who edits a line throws away the
 * note they left on it.
 * @param {HTMLFormElement} form
 * @param {CartLine} line
 */
function carryUnclaimed(form, line) {
  // `form.elements`, not a descendant query: the inline name block's own input sits outside the
  // form and joins it by `form=`, and carrying a name it already submits sends the property twice.
  const claimed = [...form.elements].map((element) =>
    propertyName(/^properties\[(.+)\]$/.exec(element.getAttribute('name') ?? '')?.[1])
  );
  for (const [property, value] of Object.entries(unclaimedProperties(line, claimed))) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${property}]`;
    input.value = value;
    form.append(input);
  }
}

/**
 * @param {HTMLInputElement | HTMLTextAreaElement} element
 * @param {string | undefined} value
 */
function setValue(element, value) {
  if (value === undefined || element.value === value) return;
  // The line arrives a round trip after the page, so the shopper may already be typing. A control
  // that no longer holds the server value is theirs; overwriting it would snap it back.
  if (element.value !== element.defaultValue) return;
  element.value = value;
  // The inline name block rebuilds its preview, its counter and its hidden `_config` off these two
  // events, so a restored value has to arrive the way a typed one does.
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Drop the edit and leave an ordinary product page behind - the shopper removed the line in another
 * tab, checked out, or followed a stale link. Guessing at another line would reconfigure and then
 * delete something they never asked about.
 */
function forget() {
  editing = null;
  const url = new URL(window.location.href);
  url.searchParams.delete(CART_LINE_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Carry the edited line's add-ons across to the line replacing it, BEFORE the removal: Shopify
 * deletes a nested line with its parent, so afterwards the gift bag and the paid protection plan
 * are already gone. Best effort - a failure leaves a loose row, never a missing pendant.
 * @param {Cart | null | undefined} cart
 * @param {CartLine} edited
 * @param {CartLine} replacement
 * @returns {Promise<void>}
 */
async function carry(cart, edited, replacement) {
  const { addOnLines, carryAddOns } = await cartEdit();
  const before = addOnLines(cart, edited);
  if (before.native.length === 0 && before.linked.length === 0) return;
  try {
    await carryAddOns(before, replacement, true);
  } catch (error) {
    console.warn('[custom-cart-line-edit] could not carry the add-on lines across', error);
  }
}

/**
 * Settle the edit against the cart the add produced, then hand the shopper back to the cart they
 * started from. Every failure path here leaves the original line in place: a lost edit is a retry,
 * a lost line is the configuration they came to change.
 * @param {string} key
 * @param {Submission} submission
 * @param {Cart | undefined} resource - The cart as Horizon's product form read it back.
 * @returns {Promise<void>}
 */
async function settle(key, submission, resource) {
  try {
    const { changeCartLine, fetchCart, removeCartLine } = await cartEdit();
    const cart = resource?.items?.length ? resource : await fetchCart();
    const plan = planReplacement(cart, key, submission);
    if (plan.action === 'replace') {
      const edited = cart?.items?.find((item) => item.key === key);
      if (edited) await carry(cart, edited, plan.line);
      await removeCartLine(key, document.documentElement);
    } else if (plan.action === 'merged') {
      await changeCartLine(
        key,
        submission.quantity,
        plan.line.properties ?? {},
        document.documentElement
      );
    } else {
      console.warn('[custom-cart-line-edit] nothing in the cart matches the update; line kept');
    }
  } catch (error) {
    console.warn('[custom-cart-line-edit] could not replace the edited cart line', error);
  }
  markBusy(false);
  window.location.assign(Theme.routes.cart_url);
}

/** @param {Event} event */
function handleCartUpdate(event) {
  const detail = /** @type {{ resource?: Cart, data?: Record<string, unknown> }} */ (
    /** @type {CustomEvent} */ (event).detail
  );
  if (detail?.data?.source !== 'product-form-component' || detail.data.didError) return;

  const key = editing;
  const submission = submitted;
  // Spent before the round trip and whatever its outcome: a later add must never delete a line.
  editing = null;
  submitted = null;
  if (!key || !submission) return;
  markBusy(true);
  void settle(key, submission, detail.resource);
}

/** @param {Event} event */
function handleSubmit(event) {
  const form = event.target;
  if (form instanceof HTMLFormElement && form.matches(FORM_SELECTOR)) {
    submitted = readSubmission(form);
  }
}

async function boot() {
  const key = cartLineKey();
  if (!key) return;
  const form = document.querySelector(FORM_SELECTOR);
  if (!(form instanceof HTMLFormElement)) return;

  const { fetchCartLine } = await cartEdit();
  const line = await fetchCartLine(key);
  if (!line) {
    forget();
    return;
  }

  editing = key;
  restore(form, line);
  carryUnclaimed(form, line);
  dressAsUpdate(form);
  quietTheDrawer();

  // A variant change re-renders the buy form from the server, so the call to action and the carried
  // answers need putting back. The restored ones are the shopper's to change and are left alone.
  document.addEventListener(ThemeEvents.variantUpdate, () => {
    const current = document.querySelector(FORM_SELECTOR);
    if (!(current instanceof HTMLFormElement)) return;
    carryUnclaimed(current, line);
    dressAsUpdate(current);
  });
  // On the document, and in the capture phase: the form itself is replaced by that same re-render.
  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
}

if (typeof document !== 'undefined') void boot();
